import { useEffect, useId, useMemo, useRef, useState } from "react";
import { AssetImage } from "./AssetImage";
import { defaultAssetUrl, type AssetLoader } from "./images";

const pageSize = 24;

/** The same scoped asset service is used for previews and the selected image. */
export function AssetLibraryPicker({
  assets,
  value,
  label,
  disabled,
  scope,
  assetUrl = defaultAssetUrl,
  loadAsset,
  onChange,
}: {
  assets: { id: string; name: string; width: number; height: number }[];
  value?: string;
  label: string;
  disabled?: boolean;
  scope: string;
  assetUrl?: (id: string) => string;
  loadAsset?: AssetLoader;
  onChange: (id: string) => void;
}) {
  const authority = useMemo(() => ({}), [scope, disabled, loadAsset, assetUrl]);
  const [open, setOpen] = useState<object>();
  const selected = assets.find((asset) => asset.id === value);
  return (
    <>
      <button
        type="button"
        className="asset-library-open"
        aria-label={`${label} asset library`}
        aria-haspopup="dialog"
        disabled={disabled}
        onClick={() => setOpen(authority)}
      >
        Choose from asset library{selected ? ` · ${selected.name}` : ""}
      </button>
      {open === authority && !disabled && (
        <LibraryDialog
          assets={assets}
          value={value}
          label={label}
          assetUrl={assetUrl}
          loadAsset={loadAsset}
          onClose={() => setOpen(undefined)}
          onChange={onChange}
        />
      )}
    </>
  );
}

function LibraryDialog({
  assets,
  value,
  label,
  assetUrl,
  loadAsset,
  onClose,
  onChange,
}: {
  assets: { id: string; name: string; width: number; height: number }[];
  value?: string;
  label: string;
  assetUrl: (id: string) => string;
  loadAsset?: AssetLoader;
  onClose: () => void;
  onChange: (id: string) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null),
    heading = useId();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const matches = assets.filter((asset) =>
    asset.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  );
  const pages = Math.max(1, Math.ceil(matches.length / pageSize));
  const current = Math.min(page, pages - 1);
  const visible = matches.slice(current * pageSize, (current + 1) * pageSize);
  const dismiss = () => {
    dialog.current?.close();
    onClose();
  };
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => {
      if (element?.open) element.close();
    };
  }, []);
  return (
    <dialog
      ref={dialog}
      className="proof-asset-library"
      aria-labelledby={heading}
      onCancel={(event) => {
        event.stopPropagation();
        event.preventDefault();
        dismiss();
      }}
      onClose={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          event.preventDefault();
          dismiss();
        }
        if (event.key === "Enter" && event.target instanceof HTMLInputElement)
          event.preventDefault();
      }}
    >
      <style>{styles}</style>
      <header>
        <h2 id={heading}>{label} · Asset library</h2>
        <button type="button" onClick={dismiss}>
          Close library
        </button>
      </header>
      <label className="proof-asset-search">
        Search images
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setPage(0);
          }}
        />
      </label>
      <p role="status">
        {matches.length} {matches.length === 1 ? "image" : "images"}
      </p>
      {visible.length ? (
        <ul className="proof-asset-grid" aria-label="Library images">
          {visible.map((asset) => (
            <li key={asset.id}>
              <button
                type="button"
                aria-label={`Choose ${asset.name}`}
                aria-pressed={value === asset.id}
                onClick={() => {
                  dismiss();
                  onChange(asset.id);
                }}
              >
                <span className="proof-asset-preview">
                  <AssetImage
                    assetId={asset.id}
                    assetUrl={assetUrl}
                    loadAsset={loadAsset}
                    alt={asset.name}
                    retryable={false}
                  />
                </span>
                <span className="proof-asset-name">{asset.name}</span>
                <small>
                  {asset.width} × {asset.height} px
                  {value === asset.id ? " · Selected" : ""}
                </small>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p>No images match your search.</p>
      )}
      <footer>
        <button
          type="button"
          disabled={current === 0}
          onClick={() => setPage(current - 1)}
        >
          Previous images
        </button>
        <span>
          Page {current + 1} of {pages}
        </span>
        <button
          type="button"
          disabled={current + 1 >= pages}
          onClick={() => setPage(current + 1)}
        >
          Next images
        </button>
      </footer>
    </dialog>
  );
}

const styles = `
.proof-asset-library{box-sizing:border-box;position:fixed;inset:0;margin:auto;width:min(840px,calc(100vw - 32px));max-width:calc(100vw - 32px);max-height:calc(100dvh - 32px);padding:24px;border:1px solid #d7e2dc;border-radius:16px;background:#fff;color:#183c35;font:14px/1.5 system-ui,sans-serif;overflow:auto;box-shadow:0 24px 80px #102d3638}
.proof-asset-library::backdrop{background:#18352d70}
.proof-asset-library header,.proof-asset-library footer{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}
.proof-asset-library h2{margin:0;font-size:19px;overflow-wrap:anywhere}
.proof-asset-library button,.proof-asset-library input{box-sizing:border-box;font:inherit;color:inherit;border:1px solid #ccdcd3;border-radius:8px;background:#fff;padding:9px 12px}
.proof-asset-library button{cursor:pointer}.proof-asset-library button:disabled{cursor:default;opacity:.5}
.proof-asset-library :focus-visible{outline:3px solid #286f59;outline-offset:3px}
.proof-asset-search{display:grid;gap:6px;margin-top:20px}.proof-asset-search input{width:100%}
.proof-asset-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(150px,100%),1fr));gap:12px;padding:0;list-style:none;margin:16px 0 24px}
.proof-asset-grid li{min-width:0}.proof-asset-grid button{display:flex;flex-direction:column;gap:8px;text-align:left;width:100%;height:100%;padding:8px}
.proof-asset-grid button:hover{background:#f2f7f3;border-color:#7a9c89}.proof-asset-grid button[aria-pressed=true]{border:2px solid #286f59;background:#eef6f0;padding:7px}
.proof-asset-preview{display:flex;align-items:center;justify-content:center;width:100%;height:120px;border-radius:5px;background:repeating-conic-gradient(#edf0ed 0% 25%,#fff 0% 50%) 0/16px 16px;overflow:hidden;font-size:12px}
.proof-asset-preview img{display:block;max-width:100%;max-height:100%;object-fit:contain}.proof-asset-name{overflow-wrap:anywhere;font-weight:600}.proof-asset-grid small{font-size:11px;color:#5a7368}
`;
